import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Collapse,
  Divider,
  Badge,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Celebration as EngageIcon, Settings } from '@mui/icons-material';
import ApprovalIcon from '@mui/icons-material/ThumbUpAltOutlined';         // For "Proof Approvals"
import PayslipIcon from '@mui/icons-material/DescriptionOutlined';         // For "Salary & Payslip"
// import Form16Icon from '@mui/icons-material/Assignment';           // (optional, you can skip now)
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Menu as MenuIcon,
  ChevronLeft,
    ReceiptOutlined as ReceiptOutlinedIcon,

  WorkOutline as RecruitmentIcon,
  PeopleOutline as PeopleIcon,
  MonetizationOnOutlined as PayrollIcon,
  RequestPageOutlined as RequestIcon,
  EventNoteOutlined as LeaveIcon,
  AccessTimeOutlined as AttendanceIcon,
  SchoolOutlined as TrainingIcon,
  AssessmentOutlined as PerformanceIcon,
  PersonAddOutlined as AddPersonIcon,
  NotificationsOutlined as NotificationsIcon,
  ReceiptOutlined as Form16Icon,
  VisibilityOutlined as ViewIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SearchOutlined as Search,
  AssignmentOutlined as AssessmentIcon,
  Rule,
  DevicesOutlined as DevicesIcon,
  LockOutlined as LockOutlinedIcon,
  DomainOutlined as DomainIcon,
} from '@mui/icons-material';

import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import ViewKanbanIcon from '@mui/icons-material/ViewKanbanOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import ListAltIcon from '@mui/icons-material/ListAltOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import TimelineIcon from '@mui/icons-material/TimelineOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Sidebar = ({ open: propOpen, setOpen: setPropOpen, currentSubscription }) => {
  const [open, setOpen] = useState(propOpen !== undefined ? propOpen : true);
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role")?.toLowerCase();
  const isLoggedIn = !!token;
  const role = userRole || null;

  // Helper to read initial subscription from localStorage or props
  const readInitialSubscription = () => {
    if (currentSubscription) return currentSubscription;
    const storedSub = localStorage.getItem("subscription");
    if (storedSub) {
      try {
        return JSON.parse(storedSub);
      } catch (e) {
        //console.error("Failed to parse stored subscription", e);
      }
    }
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.subscription) return user.subscription;
      } catch (e) {
        //console.error("Failed to parse user subscription", e);
      }
    }
    return null;
  };

  const [subscription, setSubscription] = useState(readInitialSubscription());

  useEffect(() => {
    if (propOpen !== undefined) {
      setOpen(propOpen);
    }
  }, [propOpen]);

  useEffect(() => {
    if (currentSubscription) {
      setSubscription(currentSubscription);
    }
  }, [currentSubscription]);

  useEffect(() => {
    const handleAuthChange = () => {
      setSubscription(readInitialSubscription());
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  // Normalization helper to standardize codes, names, spaces, ampersands, and special characters
  const normalizeSubModuleString = (str) => {
    if (!str) return "";
    return String(str)
      .toUpperCase()
      .replace(/&/g, " AND ")
      .replace(/[^A-Z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  // Safe Property Extractor supporting all BCC object keys (code, sub_module_code, name, sub_module_name, title, etc.)
  const extractSubModuleValue = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return (
      item.sub_module_code ||
      item.code ||
      item.subModuleCode ||
      item.sub_module_name ||
      item.subModuleName ||
      item.name ||
      item.module_name ||
      item.title ||
      ""
    );
  };

  // Mapping of PulseHR submodule codes to all possible BCC short codes, display names, and aliases
  const SUBMODULE_CODE_ALIASES = {
    RECRUITMENT: [
      "RECRUITMENT", "REC_01", "ONBOARDING", "REC", "RECRUITMENT_ONBOARDING",
      "RECRUITMENT & ONBOARDING", "RECRUITMENT AND ONBOARDING",
    ],
    CORE_HR: [
      "CORE_HR", "COREHR", "EMP", "EMP_01", "EMPLOYEE", "HR_CORE", "HR",
      "EMPLOYEE_MANAGEMENT", "EMPLOYEE MANAGEMENT", "CORE HR", "CORE_HR_MANAGEMENT", "EMPLOYEES"
    ],
    EMPLOYEE_MANAGEMENT: [
      "EMPLOYEE_MANAGEMENT", "EMPLOYEE MANAGEMENT", "CORE_HR", "COREHR", "EMP", "EMP_01",
      "EMPLOYEE", "HR_CORE", "HR", "CORE HR", "CORE_HR_MANAGEMENT", "EMPLOYEES"
    ],
    TRAINING: [
      "TRAINING", "TRAIN", "TRAIN_01", "LEARNING", "KNOWLEDGE_MANAGEMENT", "TRAINING_MANAGEMENT", 
      "TRAINING_DEVELOPMENT", "TRAINING & DEVELOPMENT", "TRAINING AND DEVELOPMENT",
    
    ],
    LEAVE: [
      "LEAVE", "LEAVES", "LEA_01", "LEAVE_MANAGEMENT",
      "LEAVES", 
    ],
    ATTENDANCE: [
      "ATTENDANCE", "ATT", "ATT_01", "ATTENDANCE_MANAGEMENT", "TIMESHEET",
  
    ],
    PERFORMANCE: [
      "PERFORMANCE", "PERF_01", "PERFOR", "PERFORMANCE_MANAGEMENT", "APPRAISAL",
      
    ],
    BASIC_PROJECTS: [
      "BASIC_PROJECTS", "PROJ", "PROJ_01", "PROJECT", "PROJECTS",
      "PROJECT_MANAGEMENT", "BASIC PROJECTS", "PROJECT MANAGEMENT"
    ],
    PROJECT_MANAGEMENT: [
      "PROJECT_MANAGEMENT", "BASIC_PROJECTS", "PROJ", "PROJ_01", "PROJECT", "PROJECTS",
      "BASIC PROJECTS", "PROJECT MANAGEMENT"
    ],
    PAYROLL_COMPLIANCE: [
      "PAYROLL_COMPLIANCE", "PAYROLL_AND_COMPLIANCE", "PAYROLL", "PAY", "PAY_01", "PAY_08",
      "SALARY", "COMPLIANCE", "PAYROLL_MANAGEMENT", "PAYROLL & COMPLIANCE", "PAYROLL AND COMPLIANCE"
    ],
    PAYROLL: [
      "PAYROLL", "PAYROLL_COMPLIANCE", "PAYROLL_AND_COMPLIANCE", "PAY", "PAY_01", "PAY_08",
      "SALARY", "COMPLIANCE", "PAYROLL_MANAGEMENT", "PAYROLL & COMPLIANCE", "PAYROLL AND COMPLIANCE"
    ],
    ADVANCED_CPO_ANALYTICS: [
      "ADVANCED_CPO_ANALYTICS", "ADVANCED_CPO_AND_ANALYTICS", "CPO", "ANALYTICS", "COST_CENTER", "COST_CENTRE",
      "CPO_ANALYTICS", "ADVANCED_ANALYTICS", "COST CENTER", "ADVANCED CPO ANALYTICS", "COST_CENTER_MANAGEMENT"
    ],
    COST_CENTER: [
      "COST_CENTER", "COST_CENTRE", "ADVANCED_CPO_ANALYTICS", "ADVANCED_CPO_AND_ANALYTICS", "CPO", "ANALYTICS",
      "CPO_ANALYTICS", "ADVANCED_ANALYTICS", "COST CENTER", "ADVANCED CPO ANALYTICS", "COST_CENTER_MANAGEMENT"
    ],
    // Backward compatibility for combined bundles
    ATTENDANCE_LEAVES: [
      "ATTENDANCE_LEAVES", "ATTENDANCE_AND_LEAVES", "ATT", "LEAVE", "ATT_01", "LEA_01",
      "ATTENDANCE", "LEAVES", "ATT_LEA", "LEAVE_MANAGEMENT", "ATTENDANCE_MANAGEMENT",
      "ATTENDANCE & LEAVES", "ATTENDANCE AND LEAVES", "LEAVE & ATTENDANCE", "LEAVE AND ATTENDANCE"
    ],
    RECRUITMENT_PERFORMANCE: [
      "RECRUITMENT_PERFORMANCE", "RECRUITMENT_AND_PERFORMANCE", "RECRUITMENT", "PERFORMANCE",
      "PERFOR", "TRAIN", "REC_01", "PERF_01", "TRAIN_01", "ONBOARDING", "TRAINING",
      "RECRUITMENT & PERFORMANCE", "RECRUITMENT & ONBOARDING", "RECRUITMENT AND ONBOARDING",
      "RECRUITMENT_AND_ONBOARDING", "PERFORMANCE_MANAGEMENT"
    ]
  };

  // Dynamic Sub-Module Check against BCC-provided allowed_sub_modules / enabled_modules from DB tables
  const isModuleUnlocked = (subModuleCode) => {
    // Foundational / Universal features (Dashboard, Connect Hub, Support, Settings, Audit Logs) have no subModuleCode
    if (!subModuleCode) return true;

    // Check directly against allowed_sub_modules / enabled_modules array returned dynamically from BCC tables
    const rawAllowed =
      subscription?.allowed_sub_modules ||
      subscription?.enabled_modules ||
      subscription?.license?.enabled_modules;

    // Default to true if no restrictions array is present or empty (e.g. initial full access / trial / unconstrained plan)
    if (!rawAllowed || (Array.isArray(rawAllowed) && rawAllowed.length === 0)) return true;

    if (Array.isArray(rawAllowed)) {
      // Extract and normalize all allowed identifiers returned from BCC
      const allowedNormalized = rawAllowed
        .map((item) => normalizeSubModuleString(extractSubModuleValue(item)))
        .filter(Boolean);

      const targetNormalized = normalizeSubModuleString(subModuleCode);
      const rawAliases = SUBMODULE_CODE_ALIASES[targetNormalized] || [targetNormalized];
      const normalizedAliases = rawAliases.map(normalizeSubModuleString);

      // Also create an un-replaced version without "AND" for loose matching (e.g. ATTENDANCE_LEAVES matching ATTENDANCE_AND_LEAVES)
      const matches = (alias, allowedItem) => {
        if (!alias || !allowedItem) return false;
        if (alias === allowedItem) return true;
        const aliasClean = alias.replace(/_AND_/g, "_");
        const itemClean = allowedItem.replace(/_AND_/g, "_");
        return aliasClean === itemClean || itemClean.includes(aliasClean) || aliasClean.includes(itemClean);
      };

      // Unlock if any alias matches any BCC allowed module
      return normalizedAliases.some((alias) =>
        allowedNormalized.some((allowedItem) => matches(alias, allowedItem))
      );
    }

    return true;
  };

  const [lockedNotice, setLockedNotice] = useState({
    open: false,
    message: ""
  });

  const handleLockedItemClick = (e, item) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const isAdmin = role === "admin";

    if (isAdmin) {
      window.dispatchEvent(
        new CustomEvent("openUpgradeModal", {
          detail: {
            feature: item.title || item.name,
            moduleCode: item.subModuleCode
          }
        })
      );
    } else {
      setLockedNotice({
        open: true,
        message: `"${item.title || item.name}" is locked under your organization's subscription plan. Please contact your administrator to upgrade.`
      });
    }
  };

  const [notifications, setNotifications] = useState([]);
  const [openProjects, setOpenProjects] = useState(false);
  const [openActiveProjects, setOpenActiveProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [allProjects, setAllProjects] = useState([]); 
  const [openMenus, setOpenMenus] = useState({
    recruitment: false,
    employeeManagement: false,
    payroll: false,
    employeeRequest: false,
    leave: false,
    attendance: false,
    performance: false,
    training: false,
    projectManagement: false,
    projects: false,
    activeProjects: false
  });


  useEffect(() => {
    // Load selected project from localStorage on component mount
    const savedProjectId = localStorage.getItem('selectedProject');
    const savedProjectName = localStorage.getItem('selectedProjectName');
    const savedProjectData = localStorage.getItem('selectedProjectData');
    
    if (savedProjectId) {
      setSelectedProject({
        id: savedProjectId,
        name: savedProjectName || `Project ${savedProjectId}`,
        ...(savedProjectData ? JSON.parse(savedProjectData) : {})
      });
      setSelectedProjectName(savedProjectName);
    }
  
    // Listen for project selection events
    const handleProjectSelection = (event) => {
      const { projectId, projectName, projectData } = event.detail;
      setSelectedProject({
        id: projectId,
        name: projectName,
        ...projectData
      });
      setSelectedProjectName(projectName);
    };
  
    window.addEventListener('projectSelected', handleProjectSelection);
  
    // Cleanup event listener
    return () => {
      window.removeEventListener('projectSelected', handleProjectSelection);
    };
  }, []);
  
  // Update your handleActiveProjectsClick function
  const handleActiveProjectsClick = () => {
    // Navigate to active projects page
    navigate('/active-projects');
    
    // Check if there's a selected project in localStorage
    const savedProjectId = localStorage.getItem('selectedProject');
    const savedProjectName = localStorage.getItem('selectedProjectName');
    
    if (savedProjectId) {
      setSelectedProject({
        id: savedProjectId,
        name: savedProjectName || `Project ${savedProjectId}`
      });
      setSelectedProjectName(savedProjectName);
    }
  };


    const handleActiveProjectsEmployeeClick = () => {
    // Navigate to active projects page
    navigate('/active-projects-employee');
    
    // Check if there's a selected project in localStorage
    const savedProjectId = localStorage.getItem('selectedProject');
    const savedProjectName = localStorage.getItem('selectedProjectName');
    
    if (savedProjectId) {
      setSelectedProject({
        id: savedProjectId,
        name: savedProjectName || `Project ${savedProjectId}`
      });
      setSelectedProjectName(savedProjectName);
    }
  };
  
  // Add a function to clear selected project (optional)
  const clearSelectedProject = () => {
    setSelectedProject(null);
    setSelectedProjectName(null);
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('selectedProjectName');
    localStorage.removeItem('selectedProjectData');
  };
  // Fetch project data from API
  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projectId}`,{
        headers:{
          Authorization:`Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.status === "success") {
        return response.data.data;
      }
      return null;
    } catch (error) {
      //console.error("Error fetching project details:", error);
      return null;
    }
  };

  // Effect to handle project selection from URL or localStorage
  useEffect(() => {
    const loadProjectData = async () => {
      // Check if the URL contains a project ID
      const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
      
      if (projectMatch && projectMatch[1]) {
        const projectId = projectMatch[1];
        
        // First, try to get from localStorage
        try {
          const storedProject = localStorage.getItem("selectedProject");
          let projectData = {};
          
          try {
            projectData = JSON.parse(localStorage.getItem("projectData") || "{}");
          } catch (error) {
            //console.error("Error parsing project data:", error);
            projectData = {};
          }
          
          // Use the project ID from URL if it exists
          const currentProjectId = projectId || storedProject;
          
          if (currentProjectId) {
            // First check if we already have this project in localStorage
            if (projectData[currentProjectId]) {
              setSelectedProject({
                id: currentProjectId,
                name: projectData[currentProjectId].name || `Project ${currentProjectId}`
              });
              setSelectedProjectName(projectData[currentProjectId].name || `Project ${currentProjectId}`);
            } else {
              // If not in localStorage, fetch from API
              const projectDetails = await fetchProjectDetails(currentProjectId);
              
              if (projectDetails) {
                // Update with actual project name from database
                setSelectedProject({
                  id: currentProjectId,
                  name: projectDetails.name || `Project ${currentProjectId}`
                });
                setSelectedProjectName(projectDetails.name || `Project ${currentProjectId}`);
                
                // Update localStorage for future use
                const newProjectData = { ...projectData };
                newProjectData[currentProjectId] = {
                  id: currentProjectId,
                  name: projectDetails.name,
                  // Add other relevant project details as needed
                };
                localStorage.setItem("projectData", JSON.stringify(newProjectData));
              } else {
                // Fallback if API call fails
                setSelectedProject({
                  id: currentProjectId,
                  name: `Project ${currentProjectId}`
                });
                setSelectedProjectName(`Project ${currentProjectId}`);
              }
            }
            
            // Auto-expand the menus when a project is selected
            setOpenMenus(prev => ({
              ...prev,
              projectManagement: true,
              projects: true,
              activeProjects: true,
              [`project-${currentProjectId}`]: true  // Ensure the project submenu is open
            }));
          }
        } catch (error) {
          //console.error("Error handling project selection:", error);
          
          // Attempt to fetch from API as fallback
          const projectDetails = await fetchProjectDetails(projectId);
          
          if (projectDetails) {
            setSelectedProject({
              id: projectId,
              name: projectDetails.name || `Project ${projectId}`
            });
            setSelectedProjectName(projectDetails.name || `Project ${projectId}`);
          } else {
            setSelectedProject({
              id: projectId,
              name: `Project ${projectId}`
            });
            setSelectedProjectName(`Project ${projectId}`);
          }
          
          // Auto-expand the menus when a project is selected
          setOpenMenus(prev => ({
            ...prev,
            projectManagement: true,
            projects: true,
            activeProjects: true,
            [`project-${projectId}`]: true  // Ensure the project submenu is open
          }));
        }
      } else {
        // No project in URL, but check if there's one in localStorage
        const storedProject = localStorage.getItem("selectedProject");
        if (storedProject) {
          try {
            const projectData = JSON.parse(localStorage.getItem("projectData") || "{}");
            
            if (projectData[storedProject]) {
              setSelectedProject({
                id: storedProject,
                name: projectData[storedProject].name || `Project ${storedProject}`
              });
              setSelectedProjectName(projectData[storedProject].name || `Project ${storedProject}`);
            } else {
              // If not in localStorage, fetch from API
              const projectDetails = await fetchProjectDetails(storedProject);
              
              if (projectDetails) {
                setSelectedProject({
                  id: storedProject,
                  name: projectDetails.name || `Project ${storedProject}`
                });
                setSelectedProjectName(projectDetails.name || `Project ${storedProject}`);
                
                // Update localStorage for future use
                const newProjectData = JSON.parse(localStorage.getItem("projectData") || "{}");
                newProjectData[storedProject] = {
                  id: storedProject,
                  name: projectDetails.name,
                  // Add other relevant project details as needed
                };
                localStorage.setItem("projectData", JSON.stringify(newProjectData));
              } else {
                // Fallback
                setSelectedProject({
                  id: storedProject,
                  name: `Project ${storedProject}`
                });
                setSelectedProjectName(`Project ${storedProject}`);
              }
            }
            
            // Also expand the necessary menus
            if (location.pathname.includes('/project/')) {
              setOpenMenus(prev => ({
                ...prev,
                projectManagement: true,
                projects: true,
                activeProjects: true,
                [`project-${storedProject}`]: true
              }));
            }
          } catch (error) {
            //console.error("Error parsing project data:", error);
            setSelectedProject(null);
          }
        } else {
          setSelectedProject(null);
          setSelectedProjectName("");
        }
      }
    };
    
    loadProjectData();
  }, [location.pathname]);

  const handleMenuToggle = (menu) => {
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };
  


 
  if (!isLoggedIn) {
    return null;
  }

  const toggleSidebar = () => {
    const nextState = !open;
    setOpen(nextState);
    if (setPropOpen) {
      setPropOpen(nextState);
    }
  };

  const rolePermissions = {
    hr: ["support","recruitment", "employeeManagement", "employeeRequest", "attendance", "training","leave","performance","payroll" ],
    "hr-manager": ["support","recruitment", "employeeManagement", "employeeRequest", "attendance", "training","leave","performance","payroll" ],
    "hiring-manager": ["recruitment"],
    "project-manager": ["support", "leave", "attendance", "performance", "projectManagement"],
    employee: ["support","employeeManagement", "employeeRequest", "leave", "performance", "attendance", "payroll", "recruitment", "training","projectManagement"],
    security:["badge"],
    finance:["support","payroll"],
    finance_manager:["support","payroll","cost-center"],
    admin:["payroll",'employeeManagement','settings'],
    "product-manager":["projectManagement"],
    "product-owner":["projectManagement"],
    "cpo":["projectManagement","cost-center","recruitment","employeeManagement","leave","attendance"],
  };

  const isHR = role === "hr" || role === "hr-manager";
  const isManager = role === "project-manager" || role === "hr" || role === "hr-manager";
  const isEmployee = role === "employee";
  const isfinance = role==="finance" || role==="ceo" || role==="finance_manager";
  const getMenuItems = () => {
    const items = [];
    if (role === "finance_manager") {
      items.push({
        title: "Dashboard",
        icon: <DashboardIcon />,
        key: "dashboard",
        path: "/home",  
      });
      items.push({
        title: "Connect Hub",
        icon: <EngageIcon />,
        key: "engage",
        submenu: [
          { name: "Milestones", path: "/engage/milestones" },
          { name: "Social Hub", path: "/engage/feeds" },
        ]
      });
      items.push({
        title: "Salary Authorization",
        icon: <PaymentsIcon />,
        key: "salary-authorization",
        path: "/salary-authorization-manager",
        subModuleCode: "PAYROLL_COMPLIANCE",
      });
      items.push({
        title: "Cost Center Dashboard",
        icon: <AccountBalanceWalletIcon />,
        key:'cost-center',
        path: "/cost-center-dashboard",
      });
      return items;
    }

    if (role === "admin") {
      items.push({
        title: "Company Details",
        icon: <DomainIcon />,
        key: "companyDetails",
        path: "/companydetails",
      });
      items.push({
        title: "Manage Cost Centers",
        icon: <AccountBalanceWalletIcon />,
        key: "manageCostCenters",
        path: "/manage-cost-centers",
      });
    }

    if (role !== "admin") {
      items.push({
        title: "Dashboard",
        icon: <DashboardIcon />,
        key: "dashboard",
        path: "/home",  
      });
      items.push({
        title: "Connect Hub",
        icon: <EngageIcon />,
        key: "engage",
        submenu: [
          { name: "Milestones", path: "/engage/milestones" },
          { name: "Social Hub", path: "/engage/feeds" },
        ]
      });
    }

    // Recruitment Section
    if (rolePermissions[role]?.includes("recruitment")) {
      items.push({
        title: "Recruitment & Onboarding",
        icon: <RecruitmentIcon />,
        key: "recruitment",
        subModuleCode: "RECRUITMENT",
        submenu: isHR ? [
          { name: "Job Posting", path: "/job-posting" },
          { name: "Job View", path: "/jobview" },
          { name: "Job Referral", path: "/refer" },
          { name: "Candidate Filter", path: "/candidates" },
          { name: "Interview Scheduling", path: "/interview-scheduling" },
          { name: "Interview Status", path: "/status" },
          { name: "Create Offer", path: "/offer" },
          { name: "Offer Management", path: "/sendoffer" },
          { name: "Onboarding Process", path: "/onboarding" },
          { name: "Onboarding List", path: "/OnboardingList" },
          { name: "Report", path: "/recreport" },
        ] : (role === "hiring-manager") ? [
       
          { name: "Job View", path: "/jobview" },
          { name: "Candidate Filter", path: "/candidates" },
          { name: "Interview Scheduling", path: "/interview-scheduling" },
          { name: "Interview Status", path: "/status" },
          { name: "Offer Approval", path: "/offerapproval" },
        ] : isEmployee ? [
          { name: "Document Upload", path: "/upload" },
          { name: "Job View", path: "/jobview" },
          { name: "Job Referral", path: "/refer" },
        ] : role === "cpo" ? [
          { name: "Report", path: "/recreport" }
        ] : []
      });
    }

    // Employee Management Section
    if (rolePermissions[role]?.includes("employeeManagement")) {
      items.push({
        title: "Employee Management",
        icon: <PeopleIcon />,
        key: "employeeManagement",
        subModuleCode: "CORE_HR",
        submenu: isHR ? [
          { 
            name: "Contractors", 
            path: null,
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Add Contractors/Consultant", path: "/contract"},
              { name: "Add Contract Employee", path: "/contractemp"},
              { name: "View Contract Employee's", path: "/contractlist"},
            ]
          },
          { 
            name: "Add Employee", 
            path: null,
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Add Employee Details", path: "/alldetails" },
            ]
          },
          { 
            name: "View Employees", 
            path: null,
            icon: <ViewIcon />,
            submenu: [
              { name: "View Employee Details", path: "/viewpersonal" },
              { name: "View Roles", path: "/viewrole" },
            ]
          },
          { 
            name: "Employee Search", 
            path: "/search",
            icon: <Search />
          },
          {
            name:"Employee request",
            path:null,
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Bonafide Request list", path: "/bonafiedlist" },
              { name: "Advance Salary Request list", path: "/salaryrequest" },
              { name: "Experience Letter Request list", path: "/experience-letter-request" },
            ]
          },
          {
            name:"Training",
            icon: <TrainingIcon />,
            subModuleCode: "TRAINING",
            submenu: [
              { name: "Gap Analysis", path: "/gap-analysis" },
              { name: "Knowledge Management", path: "/training" },
              { name: "Training List", path: "/trainings" },
              { name: "Add Videos", path: "/video" },
              { name: "Add Test Questions", path: "/test" }
            ]
          },
          {
            name: "Feedback",
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Employee Feedback", path: "/all-feedback" }
            ]
          },
          { name: "Asset Tracker", path: "/assets", icon: <DevicesIcon /> },      
          { name: "Offboarding", path: "/offboarding", icon: <Rule /> },
           { name: "Cost Center Management", path: "/hr-cost-centers", icon: <AccountBalanceWalletIcon /> },
{ name: "Attrition", path: "/attrition", icon: <TrendingDownIcon /> },
          { name: "Report", path: "/empreport" },
         
        ] : role === 'admin' ? [
          { name: "Add Employee Details", path: "/addemppersonal" },
        ] : role === "employee" ? [
          {
            name: "Add Details",
            path: null,
            icon: <AddPersonIcon/>,
            submenu: [
              { name: "Add Personal Details", path: "/candidate-personal" },
              { name: "Add Financial Details", path: "/candidate-financial" },
              { name: "Skills & Expertise", path: '/skills-expertise' }
            ]
          },
          {
            name: "Employee Request",
            path: null,
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Bonafide Request", path: "/bonafied" },
              { name: "Advance Salary Request", path: "/advancesalary" },
              { name: "Experience Letter Request", path: "/experience-letter" },
            ]
          },
          {
            name: "Training",
            icon: <AddPersonIcon />,
            subModuleCode: "TRAINING",
            submenu: [
              { name: "View training", path: "/updatetraining" }
            ]
          },
          {
            name: "Feedback",
            icon: <AddPersonIcon />,
            submenu: [
              { name: "Give Feedback", path: "/empfeedback" },
            ]
          },
          { name: "Assets Tracker", path: "/my-assets", icon: <DevicesIcon /> },
          { name: "My Offboarding", path: "/offboarding", icon: <Rule /> },
        ] : role === "cpo" ? [
          { name: "Report", path: "/empreport" }
        ] : []
      });
    }

    if (rolePermissions[role]?.includes("payroll")) {
      items.push({
        title: "Payroll",
        icon: <PayrollIcon />,
        key: "payroll",
        subModuleCode: "PAYROLL_COMPLIANCE",
        submenu: isfinance ? [
          {
            name: "Salary And Payslip",
            path: null,
            icon: <PayslipIcon />, 
            submenu: [
              { name: "Upload CTC details", path: "/upload-salary" },
              { name: "Payroll Calculation", path: "/payroll-calculation" },
              { name: "Payroll Approval", path: "/finance/payroll-approval" },
              { name: "Generate Payslip", path: "/generate-payslip" },
              { name: "Salary Authorization", path: role === 'finance_manager' ? '/salary-authorization-manager' : '/salary-disbrusement' },
              { name: "Arrears", path: "/employee-payment" },
            ]
          },
          {
            name: "Proof Approvals",
            path: null,
            icon: <ApprovalIcon />, 
            submenu: [
              { name: "Form12BB", path: "/viewform12bb" },
              { name: "Allowance Proof Approval", path: "/allowanceproofapproval" },
              { name: "Investment & HRA Proof Approval", path: "/proofapproval" },
              { name: "FormT Approval", path: '/form-t-approval' }
            ]
          },
          {
            name: "Pro Forma Invoice",
            path: null,
            icon: <ReceiptOutlinedIcon />,
            submenu: [
              { name: "Generate Invoice", path: "/proforma-invoice" },
              { name: "Invoice History", path: "/proforma-history" }
            ]
          },
          {
            name: "Compliance",
            path: null,
            icon: <Rule/>,
            submenu: [
              { name: "Minimum Wage Compliance", path: "/min-wage" },
              { name: "Statutory Returns", path: "/compliance" }
            ]
          },
          {
            name: "Forms",
            path: null,
            icon: <DescriptionIcon />,
            submenu: [
              {
                name: "Form16",
                path: null,
                icon: <Form16Icon />,
                submenu: [
                  { name: "Form16 (Part A)", path: "/form16-partA" },
                  { name: "Form16 (Part B)", path: "/form16-partB" },
                ]
              },
            ]
          },
          {
            name: "Tax Reports",
            path: null,
            icon: <PaymentsIcon />,
            submenu: [
              { name: "Payroll Register", path: "/payroll-register" },
              { name: "Salary Revisions", path: '/salary-revision' },
              { name: "Payroll Report", path: "/payroll-report" },
              { name: "Gender-Wise Expenditure", path: '/expenditure-report' },
              { name: "Gratuity Overview", path: '/gratuity-overview-report' },
              { name: "Monthly Tax (Specific)", path: "/monthly-tax-specific" },
              { name: "Yearly Tax (Specific)", path: "/yearly-tax-specific" },
              { name: "Monthly Tax (All)", path: "/monthly-tax-all" },
              { name: "Yearly Tax (All)", path: "/yearly-tax-all" }
            ]
          },
        ] : role === "employee" ? [
          {
            name: "Transactions",
            path: null,
            icon: <PaymentsIcon />,
            submenu: [
              { name: "Investment Declaration", path: "/investmentdec" },
              { name: "Investment & HRA Proof", path: "/investmentproof" }
            ]
          },
          {
            name: "Financial",
            path: null,
            icon: <PaymentsIcon />,
            submenu: [
              { name: "Allowance Claim", path: "/allowanceclaim" },
              { name: "View CTC Details", path: "/view-ctc" },
              { name: "Projected Tax Details", path: "/projected-tax" },
              { name: "Gratuity Overview", path: '/gratuity-overview' },
              { name: "Employee Payslip", path: "/view-payslip" },
            ]
          },
          { name: "Form 16", path: "/form16" },
          { name: "Form12BB", path: "/form12bb" },
          { name: "Form T", path: '/form-t' }
        ] : isHR ? [
          { name: "HR Payroll Processing", path: "/hr-payroll-processing" },
          { name: "Form T", path: '/form-t' },
          { name: "Salary Transfer", path: '/salary-export' },
        
        ] : role === 'admin' ? [
          { name: "Company Payroll Settings", path: "/company-payroll-settings" },
        ] : []
      });
    }

    if (rolePermissions[role]?.includes("leave")) {
      items.push({
        title: "Leave",
        icon: <LeaveIcon />,
        key: "leave",
        subModuleCode: "LEAVE",
        submenu: role === "project-manager" ? [
          { name: "Leave Requests", path: "/manager" },
          { name: "Holiday Calendar", path: "/holiday" },
          { name: "Team On Leave", path: "/team-on-leave" },
          { name: "Leave Prediction", path: "/leaveprediction" }, 
          { name: "Report", path: "/leavereport" },
        ] : role === "employee" ? [
          { name: "Apply for Leave", path: "/leave" },
          { name: "Holiday Calendar", path: "/holiday" }
        ] : isHR ? [
          { name: "Leave Policies", path: "/leavepolicies" },
          { name: "Holiday Details", path: "/holidaydetails" }, 
          { name: "Leave Prediction", path: "/leaveprediction" }, 
          { name: "Report", path: "/leavereport" },
        ] : role === "cpo" ? [
          { name: "Report", path: "/leavereport" }
        ] : []
      });
    }

    // Attendance Section
    if (rolePermissions[role]?.includes("attendance")) {
      items.push({
        title: "Attendance",
        icon: <AttendanceIcon />,
        key: "attendance",
        subModuleCode: "ATTENDANCE",
        submenu: role === "employee" ? [
          { name: "Manual Attendance", path: "/manual-entry" },
          { name: "Attendance Calender ", path: "/attendance-view" },
          { name: "Timesheet", path: "/timesheet" },
        ] : role === 'project-manager' ? [
          { name: "TimeSheet Approval", path: '/time-sheet-approval' },
          { name: "Team Attendance", path: "/manager/team-attendance" },
          { name: "Attendance Regularization", path: "/manager-regularization-Approval" },
          { name: "Report", path: "/attendencereport" }
        ] : role === "cpo" ? [
          { name: "Report", path: "/attendencereport" }
        ] : [
          { name: "Attendance Records", path: "/attendance" },
          { name: "Report", path: "/attendencereport" }
        ]
      });
    }

    // Performance Section
    if (rolePermissions[role]?.includes("performance")) {
      items.push({
        title: "Performance",
        icon: <PerformanceIcon />,
        key: "performance",
        subModuleCode: "PERFORMANCE",
        submenu: role === "project-manager" ? [
          { name: "Team Contributions", path: "/manager/team-contributions" },
          { name: "Performance Management", path: "/performance" },
          { name: "Self-Appraisal Reviews", path: "/appraisal-cycle-manager" },
          { name: "Report", path: "/perreport" }
        ] : role === "employee" ? [
          { name: "View Performance", path: "/view" },
          { name: "Self-Appraisal", path: "/self-appraisal-cycle" },
        ] : isHR ? [
          { name: "Appraisal Cycle", path: "/appraisal-cycle-hr" },
        ] : []
      });
    }

    if (rolePermissions[role]?.includes("projectManagement")) {
      items.push({
        title: "Project Management",
        icon: <AssignmentIcon />,
        key: "projectManagement",
        subModuleCode: "BASIC_PROJECTS",
        submenu: (role === "cpo") ? [
          {
            name: "Dashboard",
            path: "/cpo-ppm-dashboard",
            icon: <DashboardIcon />
          },
          {
            name: "Multi-Project Dashboard",
            path: "/portfolio-health",
            icon: <SpeedIcon />
          },
          {
            name: "NPI Project Intake",
            path: "/npi-intake",
            icon: <AssignmentIcon />
          },
          {
            name: "Projects",
            path: null,
            icon: <AssignmentIcon />,
            submenu: [
              { name: "All Projects", path: "/pro" },
              { name: "Data Migration", path: "/project-migration" },
              { name: "Quotation Management", path: '/generate-quotation' },
                            { name: "AI Document Generator", path: "/frdtrd" },

              {
                name: "Active Projects",
                path: "/active-projects",
                key: "activeProjects",
                onClick: handleActiveProjectsClick,
                ...(selectedProject ? {
                  submenu: [
                    {
                      name: selectedProjectName || selectedProject.name || `Project ${selectedProject.id}`,
                      path: null,
                      key: `project-${selectedProject.id}`,
                      submenu: [
                        { name: "Roadmap", path: `/project/${selectedProject.id}/roadmap` },
                        { name: "Backlog", path: `/project/${selectedProject.id}/backlog`, key: `project-${selectedProject.id}-backlog` },
                        {
                          name: "Sprint",
                          key: `project-${selectedProject.id}-backlog-sprint`,
                          submenu: [
                            { name: "Active Sprint", path: `/project/${selectedProject.id}/backlog/sprint/active`, key: `project-${selectedProject.id}-backlog-sprint-active` },
                            { name: "Completed Sprints", path: `/project/${selectedProject.id}/backlog/sprint/completed`, key: `project-${selectedProject.id}-backlog-sprint-completed` }
                          ]
                        },
                        { name: "Calendar", path: `/project/${selectedProject.id}/calendar` },
                        { name: "Dashboard", path: `/project/${selectedProject.id}/db`, key: `project-${selectedProject.id}-db` }
                      ]
                    }
                  ]
                } : {})
              },
              { name: "Project Updates", path: "/proupdates" },
              { name: "Report", path: '/reports' }
            ]
          },
          {
            name: "Team Management",
            path: null,
            icon: <GroupIcon />,
            submenu: [
              { name: "Resource Planning Dashboard", path: "/resource-planning-dashboard" }
            ]
          }
        ] : (role === "product-manager") ? [
          {
            name: "Dashboard",
            path: "/cpo-ppm-dashboard",
            icon: <DashboardIcon />
          },
          {
            name: "NPI Project Intake",
            path: "/npi-intake",
            icon: <AssignmentIcon />
          },
          {
            name: "Projects",
            path: null,
            icon: <AssignmentIcon />,
            submenu: [
              { name: "All Projects", path: "/pro" },
              { name: "Create Project", path: "/create-project" },
              { name: "Quotation Management", path: '/generate-quotation' },
              { name: "AI Document Generator", path: "/frdtrd" },
              {
                name: "Active Projects",
                path: "/active-projects",
                key: "activeProjects",
                onClick: handleActiveProjectsClick,
                ...(selectedProject ? {
                  submenu: [
                    {
                      name: selectedProjectName || selectedProject.name || `Project ${selectedProject.id}`,
                      path: null,
                      key: `project-${selectedProject.id}`,
                      submenu: [
                        { name: "Roadmap", path: `/project/${selectedProject.id}/roadmap` },
                        { name: "Requirements", path: `/project/${selectedProject.id}/create-task` },
                        { name: "Resource Requirements", path: `/project/${selectedProject.id}/skill-matrix` },
                        { name: "Change Requirements", path: `/project/${selectedProject.id}/CR` },
                        { name: "Backlog", path: `/project/${selectedProject.id}/backlog`, key: `project-${selectedProject.id}-backlog` },
                        { name: "Calendar", path: `/project/${selectedProject.id}/calendar` },
                        { name: "Dashboard", path: `/project/${selectedProject.id}/db`, key: `project-${selectedProject.id}-db` }
                      ]
                    }
                  ]
                } : {})
              },
              { name: "Report", path: '/reports' }
            ]
          }
        ] : (role === "product-owner") ? [
          {
            name: "Dashboard",
            path: "/dash",
            icon: <DashboardIcon />
          },
          {
            name: "NPI Project Intake",
            path: "/npi-intake",
            icon: <AssignmentIcon />
          },
          {
            name: "Projects",
            path: null,
            icon: <AssignmentIcon />,
            submenu: [
              { name: "All Projects", path: "/pro" },
              { name: "Create Project", path: "/create-project" },
              {
                name: "Active Projects",
                path: "/active-projects",
                key: "activeProjects",
                onClick: handleActiveProjectsClick,
                ...(selectedProject ? {
                  submenu: [
                    {
                      name: selectedProjectName || selectedProject.name || `Project ${selectedProject.id}`,
                      path: null,
                      key: `project-${selectedProject.id}`,
                      submenu: [
                        { name: "Requirements", path: `/project/${selectedProject.id}/create-task` },
                        { name: "Change Requirements", path: `/project/${selectedProject.id}/CR` },
                        { name: "Backlog", path: `/project/${selectedProject.id}/backlog`, key: `project-${selectedProject.id}-backlog` },
                        {
                          name: "Sprint",
                          key: `project-${selectedProject.id}-backlog-sprint`,
                          submenu: [
                            { name: "Active Sprint", path: `/project/${selectedProject.id}/backlog/sprint/active`, key: `project-${selectedProject.id}-backlog-sprint-active` },
                            { name: "Completed Sprints", path: `/project/${selectedProject.id}/backlog/sprint/completed`, key: `project-${selectedProject.id}-backlog-sprint-completed` }
                          ]
                        },
                        { name: "Calendar", path: `/project/${selectedProject.id}/calendar` },
                        { name: "Dashboard", path: `/project/${selectedProject.id}/db`, key: `project-${selectedProject.id}-db` }
                      ]
                    }
                  ]
                } : {})
              }
            ]
          }
        ] : (role === "project-manager") ? [
          {
            name: "Dashboard",
            path: "/dash",
            icon: <DashboardIcon />
          },
          {
            name: "Projects",
            path: null,
            icon: <AssignmentIcon />,
            submenu: [
              { name: "All Projects", path: "/pro" },
              { name: "Create Project", path: "/create-project" },
              { name: "Data Migration", path: "/project-migration" },
              {
                name: "Active Projects",
                path: "/active-projects",
                key: "activeProjects",
                onClick: handleActiveProjectsClick,
                ...(selectedProject ? {
                  submenu: [
                    {
                      name: selectedProjectName || selectedProject.name || `Project ${selectedProject.id}`,
                      path: null,
                      key: `project-${selectedProject.id}`,
                      submenu: [
                        { name: "Roadmap", path: `/project/${selectedProject.id}/roadmap` },
                        { name: "Requirements", path: `/project/${selectedProject.id}/create-task` },
                        { name: "Resource Requirements", path: `/project/${selectedProject.id}/skill-matrix` },
                        { name: "Change Requirements", path: `/project/${selectedProject.id}/CR` },
                        { name: "Backlog", path: `/project/${selectedProject.id}/backlog`, key: `project-${selectedProject.id}-backlog` },
                        {
                          name: "Sprint",
                          key: `project-${selectedProject.id}-backlog-sprint`,
                          submenu: [
                            { name: "Active Sprint", path: `/project/${selectedProject.id}/backlog/sprint/active`, key: `project-${selectedProject.id}-backlog-sprint-active` },
                            { name: "Completed Sprints", path: `/project/${selectedProject.id}/backlog/sprint/completed`, key: `project-${selectedProject.id}-backlog-sprint-completed` }
                          ]
                        },
                        { name: "Calendar", path: `/project/${selectedProject.id}/calendar` },
                        { name: "Dashboard", path: `/project/${selectedProject.id}/db`, key: `project-${selectedProject.id}-db` }
                      ]
                    }
                  ]
                } : {})
              },
              { name: "Project Updates", path: "/proupdates" },
              { name: "Report", path: '/reports' }
            ]
          },
          {
            name: "Team Management",
            path: null,
            icon: <GroupIcon />,
            submenu: [
              { name: "Create Team", path: "/createteam" },
              { name: "Assign Team Members", path: "/assignteam" },
              { name: "Manage Team", path: "/manageteam" },
              { name: "Resource Planning Dashboard", path: "/resource-planning-dashboard" }
            ]
          }
        ] : (role === "employee") ? [
          {
            name: "Projects",
            path: null,
            icon: <AssignmentIcon />,
            submenu: [
              { name: "All Projects", path: "/project-emp" },
              {
                name: "Active Projects",
                path: "/active-projects-employee",
                key: "activeProjects",
                onClick: handleActiveProjectsEmployeeClick,
                ...(selectedProject ? {
                  submenu: [
                    {
                      name: selectedProjectName || selectedProject.name || `Project ${selectedProject.id}`,
                      path: null,
                      key: `project-${selectedProject.id}`,
                      submenu: [
                        { name: "Roadmap", path: `/project/${selectedProject.id}/roadmap` },
                        { name: "Requirements", path: `/project/${selectedProject.id}/create-task` },
                        { name: "Change Requirements", path: `/project/${selectedProject.id}/CR` },
                        { name: "Backlog", path: `/project/${selectedProject.id}/backlog`, key: `project-${selectedProject.id}-backlog` },
                        {
                          name: "Sprint",
                          key: `project-${selectedProject.id}-backlog-sprint`,
                          submenu: [
                            { name: "Active Sprint", path: `/project/${selectedProject.id}/backlog/sprint/active`, key: `project-${selectedProject.id}-backlog-sprint-active` },
                            { name: "Completed Sprints", path: `/project/${selectedProject.id}/backlog/sprint/completed`, key: `project-${selectedProject.id}-backlog-sprint-completed` }
                          ]
                        },
                        { name: "Calendar", path: `/project/${selectedProject.id}/calendar` },
                        { name: "Dashboard", path: `/project/${selectedProject.id}/db-emp`, key: `project-${selectedProject.id}-db-emp` }
                      ]
                    }
                  ]
                } : {})
              }
            ]
          }
        ] : []
      });
    }

    // Support Section (Universal)
    if (rolePermissions[role]?.includes("support")) {
      items.push({
        title: "Support",
        icon: <BugReportIcon />,
        key: "support",
        submenu: (() => {
          if (role === "employee") {
            return [
              { name: "Support Center", path: "/support-center" },
            ];
          } else if (role === "project-manager") {
            return [
              { name: "Team Tickets", path: "/manager/support-tickets" },
            ];
          } else if (isHR) {
            return [
              { name: "HR Support Tickets", path: "/hr/support-tickets" },
            ];
          } else if (role === "finance") {
            return [
              { name: "Finance Tickets", path: "/finance/support-tickets" },
            ];
          } else {
            return [];
          }
        })(),
      });
    }

    if (rolePermissions[role]?.includes("cost-center")) {
      items.push({
        title: "Cost Center",
        icon: <PaymentsIcon />,
        key: "cost-center",
        submenu: (() => {
          if (role === "cpo" || role === "principal-project-manager") {
            return [
              {
                name: "Cost Center Dashboard",
                path: "/pm-cost-center-dashboard",
              },
            ];
          } else if (role === "finance_manager") {
            return [
              {
                name: "Cost Center Dashboard",
                path: "/cost-center-dashboard",
              },
            ];
          } else {
            return [];
          }
        })(),
      });
    }
    
    if (role === "admin" || isHR) {
      items.push({
        title: "Audit Logs",
        icon: <ListAltIcon />,
        key: "audit-logs",
        path: "/admin/audit-logs"
      });
    }

    if (rolePermissions[role]?.includes("settings")) {
      items.push({
        title: "Settings",
        icon: <Settings />,
        key: "settings",
        submenu: [
          { name: "SSO Configuration", path: "/settings/sso" },
        ]
      });
    }
    
    return items;
  };

  const renderNestedSubmenu = (submenuItems, parentPath = "", level = 1, isParentLocked = false, parentModuleCode = null) => {
    if (!submenuItems) return null;
    
    return submenuItems.map((subItem) => {
      const itemKey = subItem.key || `${subItem.name.replace(/\s+/g, '')}`;
      const itemSubModuleCode = subItem.subModuleCode || parentModuleCode;
      const isSubUnlocked = isParentLocked ? false : isModuleUnlocked(itemSubModuleCode);
      
      if (subItem.submenu) {
        return (
          <React.Fragment key={itemKey}>
            <ListItem
              button
              sx={{
                pl: open ? 2 + (level * 2) : 2,
                '&:hover': { backgroundColor: '#dee3dc' },
                backgroundColor: 'transparent',
                color: isSubUnlocked ? 'inherit' : '#64748b'
              }}
              onClick={(e) => {
                if (!isSubUnlocked) {
                  handleLockedItemClick(e, { title: subItem.name, subModuleCode: itemSubModuleCode });
                  return;
                }
                if (subItem.onClick) {
                  subItem.onClick();
                } else {
                  handleMenuToggle(itemKey);
                }
              }}
              component={isSubUnlocked && subItem.path ? Link : 'div'}
              to={isSubUnlocked && subItem.path ? subItem.path : undefined}
            >
              {subItem.icon && (
                <ListItemIcon 
                  sx={{ 
                    minWidth: open ? 17 : 'auto', 
                    justifyContent: 'center', 
                    color: isSubUnlocked ? 'inherit' : '#94a3b8',
                    marginRight: '8px',
                    '& .MuiSvgIcon-root': { 
                      fontSize: '1.15rem' 
                    }
                  }}
                >
                  {subItem.icon}
                </ListItemIcon>
              )}
              <ListItemText 
                primary={open ? subItem.name : ''} 
                sx={{ 
                  '& .MuiTypography-root': { 
                    fontWeight: 'normal',
                    color: isSubUnlocked ? 'inherit' : '#64748b'
                  } 
                }}
              />
              {open && (
                !isSubUnlocked ? (
                  <LockOutlinedIcon sx={{ fontSize: '1rem', color: '#f59e0b', ml: 'auto' }} />
                ) : (
                  openMenus[itemKey] ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                )
              )}
            </ListItem>
            {isSubUnlocked && (
              <Collapse in={open && openMenus[itemKey]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {renderNestedSubmenu(subItem.submenu, subItem.path || parentPath, level + 1, false, itemSubModuleCode)}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      } else {
        const actualPath = subItem.path || (parentPath ? `${parentPath}/${subItem.name.toLowerCase()}` : '');
        
        return (
          <Tooltip 
            key={itemKey}
            title={open ? "" : `${subItem.name}${!isSubUnlocked ? ' (Locked - Upgrade Plan)' : ''}`}
            placement="right"
          >
            <ListItem
              button
              component={isSubUnlocked ? Link : 'div'}
              to={isSubUnlocked ? actualPath : undefined}
              onClick={(e) => {
                if (!isSubUnlocked) {
                  handleLockedItemClick(e, { title: subItem.name, subModuleCode: itemSubModuleCode });
                }
              }}
              sx={{
                pl: open ? 2 + (level * 2) : 2,
                '&:hover': { backgroundColor: '#dee3dc' },
                justifyContent: open ? 'flex-start' : 'center',
                color: isSubUnlocked ? 'inherit' : '#64748b',
                textDecoration: 'none',
                '& .MuiListItemText-root': { 
                  color: isSubUnlocked ? 'inherit' : '#64748b'
                }
              }}
            >
              {subItem.icon && (
                <ListItemIcon 
                  sx={{ 
                    minWidth: open ? 30 : 'auto', 
                    justifyContent: 'center', 
                    color: isSubUnlocked ? 'inherit' : '#94a3b8',
                    marginRight: '8px',
                    '& .MuiSvgIcon-root': { 
                      fontSize: '1.15rem' 
                    }
                  }}
                >
                  {subItem.icon}
                </ListItemIcon>
              )}
              <ListItemText 
                primary={open ? subItem.name : ''} 
                sx={{ 
                  '& .MuiTypography-root': { 
                    fontWeight: 'normal'
                  } 
                }}
              />
              {open && !isSubUnlocked && (
                <LockOutlinedIcon sx={{ fontSize: '1rem', color: '#f59e0b', ml: 'auto' }} />
              )}
            </ListItem>
          </Tooltip>
        );
      }
    });
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: { xs: open ? 280 : 0, md: open ? 300 : 72 },
        flexShrink: 0,
        boxSizing: 'border-box',
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: { xs: open ? 280 : 0, md: open ? 300 : 72 },
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          overflowY: 'auto', 
          marginTop: "70px",
          backgroundColor: '#f0f4f8',
          borderRight: { xs: open ? '1px solid #e0e0e0' : 'none', md: '1px solid #e0e0e0' },
          height: 'calc(100vh - 70px)',
          '&::-webkit-scrollbar': {
            width: '1px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#f0f4f8',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f0f4f8',
          }
        },
        '& a': {
          color: 'inherit',
          textDecoration: 'none'
        }
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: open ? 'space-between' : 'center', 
          p: 1,
          position: 'sticky',
          top: 0,
          backgroundColor: '#f0f4f8',
          zIndex: 1,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <IconButton onClick={toggleSidebar}>
          {open ? <ChevronLeft /> : <MenuIcon />}
        </IconButton>
      </Box>

      <List sx={{ pb: 8 }}> 
        {getMenuItems().map((item) => {
          const itemKey = item.key || item.title;
          const isUnlocked = isModuleUnlocked(item.subModuleCode);

          if (item.path) {
            return (
              <Tooltip
                key={itemKey}
                title={open ? "" : `${item.title}${!isUnlocked ? ' (Locked - Upgrade Plan)' : ''}`}
                placement="right"
              >
                <ListItem
                  button
                  component={isUnlocked ? Link : 'div'}
                  to={isUnlocked ? item.path : undefined}
                  onClick={(e) => {
                    if (!isUnlocked) {
                      handleLockedItemClick(e, item);
                    }
                  }}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: '#dee3dc' },
                    justifyContent: open ? 'flex-start' : 'center',
                    color: isUnlocked ? 'inherit' : '#64748b',
                    textDecoration: 'none'
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: open ? 6 : 'auto', 
                    justifyContent: 'center', 
                    color: isUnlocked ? 'inherit' : '#94a3b8',
                    marginRight: '8px',
                    '& .MuiSvgIcon-root': { 
                      fontSize: '1.15rem', 
                    }
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {open && <ListItemText 
                    primary={item.title} 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        fontWeight: 'normal',
                        color: isUnlocked ? 'inherit' : '#64748b'
                      } 
                    }}
                  />}
                  {open && !isUnlocked && (
                    <LockOutlinedIcon sx={{ fontSize: '1.1rem', color: '#f59e0b', ml: 'auto' }} />
                  )}
                </ListItem>
              </Tooltip>
            );
          }

          return (
            <React.Fragment key={itemKey}>
              <ListItem
                button
                onClick={(e) => {
                  if (!isUnlocked) {
                    handleLockedItemClick(e, item);
                  } else {
                    handleMenuToggle(itemKey);
                  }
                }}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: '#dee3dc' },
                  justifyContent: open ? 'flex-start' : 'center',
                  color: isUnlocked ? 'inherit' : '#64748b'
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: open ? 6 : 'auto', 
                  justifyContent: 'center', 
                  color: isUnlocked ? 'inherit' : '#94a3b8',
                  marginRight: '8px',
                  '& .MuiSvgIcon-root': { 
                    fontSize: '1.15rem', 
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText 
                  primary={item.title} 
                  sx={{ 
                    '& .MuiTypography-root': { 
                      fontWeight: 'normal',
                      color: isUnlocked ? 'inherit' : '#64748b'
                    } 
                  }}
                />}
                {open && (
                  !isUnlocked ? (
                    <LockOutlinedIcon sx={{ fontSize: '1.1rem', color: '#f59e0b', ml: 'auto' }} />
                  ) : (
                    openMenus[itemKey] ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                  )
                )}
              </ListItem>
              {isUnlocked && (
                <Collapse in={open && openMenus[itemKey]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderNestedSubmenu(item.submenu, "", 1, !isUnlocked, item.subModuleCode)}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Snackbar
        open={lockedNotice.open}
        autoHideDuration={4500}
        onClose={() => setLockedNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setLockedNotice((prev) => ({ ...prev, open: false }))}
          severity="info"
          variant="filled"
          sx={{ width: "100%", boxShadow: 3, fontWeight: 500, fontSize: "0.875rem" }}
        >
          {lockedNotice.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default Sidebar;